<template>
  <v-form
    v-model="valid"
    ref="audienceNameForm"
  >
    <v-text-field
      ref="audienceName"
      :dark="attributePanel"
      :outlined="attributePanel"
      :dense="dense"
      name="audienceName"
      :label="label"
      id="audienceName"
      :class="textClass"
      autocomplete="off"
      v-focus.lazy="true"
      @focus="targetNameFocusHandler"
      :disabled="disabled"
      v-model.trim="display_name"
      v-on:blur="targetNameOutOfFocusHandler"
      :error-messages="errorMessages"
      @input="handleInput"
    ></v-text-field>
  </v-form>
</template>

<script>
  import { focus } from 'vue-focus'
  import { validateTargetName } from '@/api'
  import OneToast from '@/components/common/toasts/OneToast'
  import { TARGET_TYPE, TARGET_ACTIONS } from '@/appLogic/targets/Constants'

  const MAX_NAME_LENGTH = 100
  const MIN_NAME_LENGTH = 6
  const Toast = new OneToast()

  const debounce = function (fn, delay) {
    let timeoutId
    return function (...args) {
      return new Promise((resolve, reject) => {
        if (timeoutId) {
          clearTimeout(timeoutId)
        }
        timeoutId = setTimeout(async () => {
          try {
            const result = await fn(...args)
            resolve(result)
          } catch (error) {
            reject(error)
          }
        }, delay)
      })
    }
  }

  const debouncedValidateTargetName = debounce(async value => {
    const response = await validateTargetName({ display_name: value })
    return response
  }, 500)

  export default {
    props: {
      classOverride: {
        type: String,
        default: '',
      },
      isNewTarget: {
        type: Boolean,
        default: false,
      },
      attributePanel: {
        type: Boolean,
        default: false,
      },
      splitSegmentName: {
        type: Boolean,
        default: false,
      },
      initialValue: {
        type: String,
        default: '',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      isValid: {
        type: Boolean,
        default: false,
      },
      validRegex: {
        default: () => /^[A-Za-z0-9_]*$/,
      },
      validFirstCharRegex: {
        default: () => /^[A-Za-z][A-Za-z0-9_]*$/,
      },
      initialTargetAction: {
        type: String,
        default: '',
      },
      displayOverride: {
        type: String,
        default: null,
      },
      validateAllNames: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        duplicateToast: null,
        display_name: this.displayOverride !== null ? this.displayOverride || '' : this.initialValue,
        nameIsValid: null,
        nameClass: null,
        isDigitalSocialDestination: false,
        currentValueFromResp: '',
        valid: false,
        outOfFocus: false,
        label: this.splitSegmentName ? 'Name' : this.attributePanel ? '' : 'Audience Name',
        dense: this.attributePanel || this.splitSegmentName,
        textClass: this.attributePanel || this.splitSegmentName ? 'left-text' : 'centered-text',
        errorMessages: [],
        lastValidatedValue: null,
        validationCache: {},
      }
    },
    mounted() {
      this.isDigitalSocialDestination = this.$parent.isDigitalSocialDestination
      if (this.display_name) {
        this.errorMessages = []
        this.valid = false // Assume not valid until checked
        this.validateAndEmit()
      }
    },
    computed: {
      originalTargetName() {
        return this.$store.state.currentTarget.currentTarget.display_name
      },
    },
    directives: {
      focus,
    },
    watch: {
      initialValue: {
        immediate: true,
        handler(value) {
          if (this.initialTargetAction === TARGET_ACTIONS.DUPLICATE) {
            this.display_name = `${this.initialValue}_COPY`
            this.$emit('updateName', this.display_name)
          } else {
            this.display_name = this.initialValue
          }
        },
      },
      '$parent.value.destinations': function () {
        this.isDigitalSocialDestination = this.$parent.isDigitalSocialDestination
      },
    },
    methods: {
      handleInput() {
        this.errorMessages = []
        this.valid = false // Assume not valid until checked
        this.validateAndEmit()
      },
      targetNameFocusHandler(e) {
        this.outOfFocus = false
        if (this.isNewTarget && this.display_name === this.initialValue) {
          if (this.initialTargetAction === TARGET_ACTIONS.DUPLICATE) {
            e.target.select()
          } else {
            this.$emit('updateName', this.display_name)
          }
        }
      },
      targetNameOutOfFocusHandler(e) {
        this.outOfFocus = true
        if (this.display_name === this.lastValidatedValue && this.validationCache[this.display_name] !== undefined) {
          // Skip validation if already validated
          return
        }
        this.errorMessages = []
        this.valid = false // Assume not valid until checked
        this.validateAndEmit()
      },
      async validateAndEmit() {
        this.$emit('updateNameIsValid', false)
        const validationResult = await this.validateDisplayName(this.display_name)
        if (validationResult !== true) {
          this.valid = false
          this.errorMessages = [validationResult]
        } else {
          this.valid = true
          this.errorMessages = []
        }
        this.emitInput()
      },
      async validateDisplayName(value) {
        if (this.disabled) {
          return true
        }

        if (value === this.lastValidatedValue && this.validationCache[value] !== undefined) {
          return this.validationCache[value] // Return cached result
        }

        if (!value && this.outOfFocus) {
          return 'Required'
        } else if (value.length > MAX_NAME_LENGTH) {
          return `Max ${MAX_NAME_LENGTH} characters`
        // } else if (value.length < MIN_NAME_LENGTH && this.outOfFocus) {
        } else if (value.length < MIN_NAME_LENGTH) {
          return `Must be at least ${MIN_NAME_LENGTH} characters`
        } else if (value.length > 0 && !this.validRegex.test(value)) {
          return 'Allowed characters are A-Z, a-z, 0-9, _'
        } else if (value.length > 0 && !this.validFirstCharRegex.test(value)) {
          return 'Must start with an alphabet'
        } else if (value === this.originalTargetName && this.isNewTarget) {
          return ''
        } else if (
          value === this.initialValue &&
          !this.isNewTarget &&
          this.initialTargetAction !== TARGET_ACTIONS.DUPLICATE
        ) {
          return true
        } else if (value.length >= MIN_NAME_LENGTH) {
          const response = await debouncedValidateTargetName(value)
          let validationResult
          if (!response.data.passNameLookup) {
            validationResult = 'Duplicate Audience Name'
          } else if (!response.data.passedPruning) {
            validationResult = 'Invalid Characters in Audience Name'
          } else {
            validationResult = true
          }

          // Cache the result and update the last validated value
          this.lastValidatedValue = value
          this.validationCache[value] = validationResult
          return validationResult
        }
      },
      emitInput() {
        this.$emit('updateNameIsValid', this.valid)
        this.$emit('updateName', this.display_name)
      },
    },
  }
</script>

<style lang="scss">
  /* Please refer to TargetFormStyles to add styles to this form */
  .centered-text input {
    text-align: center;
  }
</style>



// WEBPACK FOOTER //
// TargetName.vue?681ea3a9